<template>
  <div>
    <h3>Completed Placements</h3>

    <b-form inline @submit.prevent="" class="mb-2">
      <label for="terms">Term</label>
      <b-form-select
        id="terms"
        @input="changeTerm"
        v-model="term"
        :options="terms"
        value-field="termId"
        text-field="description"
      >
        <template slot="first">
          <!-- this slot appears above the options from 'options' prop -->
          <option :value="null">None</option>
        </template>
      </b-form-select>

      <label for="types">Type</label>
      <b-form-select
        id="types"
        v-model="opportunityTypeId"
        :options="opportunityTypes"
        value-field="opportunityTypeId"
        text-field="name"
      >
        <template slot="first">
          <!-- this slot appears above the options from 'options' prop -->
          <option :value="null">All</option>
        </template>
      </b-form-select>

      <label for="categories">Category</label>
      <b-form-select
        id="categories"
        v-model="opportunityCategoryId"
        :options="validCategoryOptions"
        value-field="opportunityCategoryId"
        text-field="name"
      >
        <template slot="first">
          <!-- this slot appears above the options from 'options' prop -->
          <option :value="null">All</option>
        </template>
      </b-form-select>

      <label for="courses">Course</label>
      <b-form-select
        id="courses"
        v-model="course"
        :options="courses"
        value-field="displayName"
        text-field="displayName"
      >
        <template slot="first">
          <!-- this slot appears above the options from 'options' prop -->
          <option :value="null">All</option>
        </template>
      </b-form-select>

      <b-input-group class="filter-group ml-2">
        <b-input-group-prepend is-text>
          <font-awesome-icon icon="search" />
        </b-input-group-prepend>
        <b-form-input
          v-model="filter"
          placeholder="Type to Search"
        ></b-form-input>
        <b-input-group-append>
          <b-button @click="filter = null">Clear</b-button>
        </b-input-group-append>
      </b-input-group>
    </b-form>

    <b-table
      striped
      hover
      :items="studentOpportunities"
      :fields="fields"
      :per-page="perPage"
      :current-page="currentPage"
      :filter="filterItems"
      @filtered="onFiltered"
      responsive
      :sort-compare="customSort"
    >
      <template slot="name" slot-scope="data">
        {{ data.item.opportunity.name }}
      </template>
      <template slot="type" slot-scope="data">
        {{
          data.item.opportunity.opportunityType
            ? data.item.opportunity.opportunityType.name
            : ""
        }}
        <div>
          {{
            data.item.opportunity.opportunityCategory
              ? "(" + data.item.opportunity.opportunityCategory.name + ")"
              : ""
          }}
        </div>
      </template>
      <template slot="site" slot-scope="data">
        {{ data.item.opportunity.site ? data.item.opportunity.site.name : "" }}
        <div
          v-if="
            data.item.opportunity.opportunityType.name == 'Translational Team'
          "
        >
          {{
            data.item.opportunity.translationalTeam
              ? "(" + data.item.opportunity.translationalTeam.name + ")"
              : ""
          }}
        </div>
      </template>
      <template slot="course" slot-scope="data">
        <div v-if="data.item.studentView">
          <div
            v-for="course in data.item.studentView.studentCourses"
            :key="course.classSid"
          >
            <p>{{ course.subjectCode }} {{ course.catalogNumber }}</p>
            <p>{{ course.unitsTaken }} credits</p>
            <p>{{ course.instructor }}</p>
          </div>
        </div>
      </template>
      <template slot="grade" slot-scope="data">
        <div v-if="data.item.studentView">
          <div
            v-for="course in data.item.studentView.studentCourses"
            :key="course.classSid"
          >
            <p>
              {{ course.gradeFinal }}
            </p>
          </div>
        </div>
      </template>
      <template slot="enrollmentDate" slot-scope="data">
        <div v-if="data.item.studentView">
          <div
            v-for="course in data.item.studentView.studentCourses"
            :key="course.classSid"
          >
            <p>
              {{ course.enrollmentAddDate | formatDate }}
            </p>
          </div>
        </div>
      </template>
      <template slot="dropDate" slot-scope="data">
        <div v-if="data.item.studentView">
          <div
            v-for="course in data.item.studentView.studentCourses"
            :key="course.classSid"
          >
            <p>
              {{
                (course.enrollmentDropDate == "2000-01-01T00:00:00" ||
                course.enrollmentDropDate == "2000-01-01T00:00:00"
                  ? ""
                  : course.enrollmentDropDate) | formatDate
              }}
            </p>
          </div>
        </div>
      </template>
      <template slot="details" slot-scope="data">
        <router-link
          :to="{
            name: 'studentOpportunityDetails',
            params: { studentOpportunityId: data.item.studentOpportunityId },
          }"
          >View</router-link
        >
      </template>
    </b-table>

    <b-pagination
      :total-rows="totalRows"
      v-model="currentPage"
      :per-page="perPage"
      align="right"
    />
  </div>
</template>

<script>
import { mapActions, mapState } from "vuex";
import terms from "../../api/terms";
import opportunities from "../../api/opportunity";
import * as _ from "lodash"; // eslint-disable-line no-unused-vars

export default {
  data() {
    return {
      fields: [
        { key: "name", label: "Name / Title", sortable: true },
        { key: "studentView.lastName", label: "Last Name", sortable: true },
        { key: "studentView.firstName", label: "First Name", sortable: true },
        { key: "course", sortable: true },
        { key: "grade", sortable: true },
        { key: "enrollmentDate", sortable: true },
        { key: "dropDate", sortable: true },
        { key: "type", sortable: true, label: "Type / Category" },
        { key: "site", label: "Site", sortable: true },
        { key: "opportunity.term.description", label: "Term", sortable: true },
        { key: "details", sortable: false },
      ],
      currentPage: 1,
      perPage: 10,
      totalRows: 0,
      filter: null,
      term: null,
      terms: [],
      opportunityTypeId: null,
      opportunityCategoryId: null,
      opportunityCategories: [],
      statuses: [],
      course: null,
      courses: [],
    };
  },

  computed: {
    ...mapState({
      studentOpportunities: (state) =>
        state.studentOpportunities.studentOpportunities,
      //terms: state => state.terms.terms,
      additionalMaterialStatuses: (state) =>
        state.studentOpportunities.additionalMaterialStatuses,
      opportunityTypes: (state) => state.studentOpportunities.opportunityTypes,
    }),
    validCategoryOptions: function () {
      var that = this;
      if (this.opportunityTypeId) {
        return _.filter(this.opportunityCategories, function (category) {
          return _.find(
            category.opportunityTypeOpportunityCategories,
            function (opportunityTypeOpportunityCategory) {
              return (
                opportunityTypeOpportunityCategory.opportunityTypeId ===
                that.opportunityTypeId
              );
            }
          );
        });
      }
      return this.opportunityCategories;
    },
  },

  methods: {
    //...mapActions("terms", ["getTermsActive"]),
    ...mapActions("studentOpportunities", [
      "getAdditionalMaterialStatuses",
      "getOpportunityTypes",
      "getCompleted",
    ]),
    changeTerm() {
      this.getCompleted(this.term);
    },
    getAdditionalMaterialFileHref(fileList) {
      var file = fileList[fileList.length - 1];
      return (
        process.env.VUE_APP_BASE_URI +
        "/api/Downloads/StudentAdditionalMaterialFile?id=" +
        file.studentOpportunityAdditionalMaterialFileId
      );
    },

    filterItems(item) {
      var matchedTerm = this.matchTerm(item, this.term);
      var matchedType = this.matchType(item, this.opportunityTypeId);
      var matchedCategory = this.matchCategory(
        item,
        this.opportunityCategoryId
      );
      var matchedCourse = this.matchCourse(item, this.course);
      var matchedFilter = this.matchFilter(item, this.filter);

      if (
        matchedTerm &&
        matchedType &&
        matchedCategory &&
        matchedCourse &&
        matchedFilter
      ) {
        return true;
      }
      return false;
    },
    matchTerm(item, term) {
      if (item.opportunity.term.termId === term || term === null) {
        return true;
      }
      return false;
    },
    matchType(item, opportunityTypeId) {
      if (
        opportunityTypeId === null ||
        item.opportunity.opportunityType.opportunityTypeId === opportunityTypeId
      ) {
        return true;
      }
      return false;
    },
    matchCategory(item, opportunityCategoryId) {
      if (
        opportunityCategoryId === null ||
        item.opportunity.opportunityCategory.opportunityCategoryId ===
          opportunityCategoryId
      ) {
        return true;
      }
      return false;
    },
    matchCourse(item, course) {
      if (
        course === null ||
        (item.studentView &&
          _.filter(item.studentView.studentCourses, (studentCourse) => {
            return (
              course ===
              studentCourse.subjectCode + " " + studentCourse.catalogNumber
            );
          }).length)
      ) {
        return true;
      }
      return false;
    },
    matchFilter(item, filter) {
      if (filter != "" && filter != null) {
        var found = this.search([item], this.filter);
        return found.length > 0;
      }
      return true;
    },
    search(collection, text) {
      var that = this;
      return _.filter(collection, function (object) {
        var search = that.deepSearch(object, text.toLowerCase());
        return _.keys(search).length > 0;
      });
    },
    deepSearch(collection, predicate, thisArg) {
      if (_.isFunction(predicate)) {
        predicate = _.iteratee(predicate, thisArg);
      } else {
        var keys = _.flatten(_.tail(arguments));
        if (typeof keys === "string") {
          keys = keys.toLowerCase();
        }
        predicate = function (val, key) {
          if (typeof val === "string") {
            val = val.toLowerCase();
          }
          return _.includes(val, keys);
        };
      }
      var that = this;
      return _.transform(collection, function (memo, val, key) {
        var include = predicate(val, key);
        if (!include && _.isObject(val)) {
          val = that.deepSearch(val, predicate);
          include = !_.isEmpty(val);
        }
        if (include) {
          _.isArray(collection) ? memo.push(val) : (memo[key] = val);
        }
      });
    },

    onFiltered(filteredItems) {
      // Trigger pagination to update the number of buttons/pages due to filtering
      this.totalRows = filteredItems.length;
      this.currentPage = 1;
    },

    customSort(a, b, key) {
      if (key === "student.lastName") {
        return a.student.lastName.localeCompare(b.student.lastName, undefined, {
          numeric: true,
        });
      } else if (key === "student.firstName") {
        return a.student.firstName.localeCompare(
          b.student.firstName,
          undefined,
          { numeric: true }
        );
      } else if (key === "category") {
        var categoryNameA = a.opportunity.opportunityCategory
          ? a.opportunity.opportunityCategory.name
          : "";
        var categoryNameB = b.opportunity.opportunityCategory
          ? b.opportunity.opportunityCategory.name
          : "";
        return categoryNameA.localeCompare(categoryNameB, undefined, {
          numeric: true,
        });
      } else if (key === "type") {
        var typeNameA = a.opportunity.opportunityType
          ? a.opportunity.opportunityType.name
          : "";
        var typeNameB = b.opportunity.opportunityType
          ? b.opportunity.opportunityType.name
          : "";
        return typeNameA.localeCompare(typeNameB, undefined, { numeric: true });
      } else if (key === "opportunity.site.name") {
        return a.opportunity.site.name.localeCompare(
          b.opportunity.site.name,
          undefined,
          { numeric: true }
        );
      } else if (key === "opportunity.term.description") {
        return a.opportunity.term.termCode < b.opportunity.term.termCode
          ? -1
          : a.opportunity.term.termCode > b.opportunity.term.termCode
          ? 1
          : 0;
      } else if (key === "completion") {
        var completionA = this.getCompletionPercentage(
          a.studentOpportunityAdditionalMaterials
        );
        var completionB = this.getCompletionPercentage(
          b.studentOpportunityAdditionalMaterials
        );
        return completionA < completionB
          ? -1
          : completionA > completionB
          ? 1
          : 0;
      }
      return null;
    },
  },

  watch: {
    terms: function () {
      if (this.term === null) {
        //this.term = this.terms[0].termId;
        var foundTerm = _.find(this.terms, (t) => {
          var start = new Date(t.termStart);
          var end = new Date(t.termEnd);
          return start.getTime() <= Date.now() && end.getTime() >= Date.now();
        });
        if (foundTerm) {
          this.term = foundTerm.termId;
        }
      }
    },
  },
  created() {
    if (this.term) {
      this.getCompleted(this.term);
    }
    terms.getTerms().then((response) => {
      this.terms = response.data;
    });
    opportunities.getInternshipCourses().then((response) => {
      this.courses = response.data;
    });
    opportunities.getOpportunityCategories().then((response) => {
      this.opportunityCategories = response.data;
    });
    this.getAdditionalMaterialStatuses();
    this.getOpportunityTypes();
  },
};
</script>

<style scoped>
.green {
  color: green;
}

.red {
  color: red;
}

.orange {
  color: orange;
}

.main-container {
  max-width: 100%;
  margin-top: 10px;
}
.widget-body {
  max-width: 100%;
}
/* .filter-group {
  margin: 10px 0 15px 0;
  max-width: 600px;
} */

.new-content-button {
  margin-left: 10px;
}

label {
  font-weight: bold;
  margin: 0 0.3em 0 1.5em;
}

.clickable {
  cursor: pointer;
}

.sort-label {
  margin-right: 0.5em;
}

.loading-div {
  text-align: center;
  font-size: 2em;
}
</style>
